import {useState} from 'react'
import {KeyboardAvoidingView, View} from 'react-native'
import Animated, {FadeIn, LayoutAnimationConfig} from 'react-native-reanimated'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {DEFAULT_SERVICE} from '#/lib/constants'
import {logEvent} from '#/lib/statsig/statsig'
import {useServiceQuery} from '#/state/queries/service'
import {useSession} from '#/state/session'
import {useLoggedOutView} from '#/state/shell/logged-out'
import {LoggedOutLayout} from '#/view/com/util/layouts/LoggedOutLayout'
import {RemoveUserForm} from '#/screens/RemoveUser/RemoveUserForm'
import {atoms as a, native} from '#/alf'
import * as Layout from '#/components/Layout'
import {ScreenTransition} from '#/components/ScreenTransition'
import noice from '../../../assets/images/noise2200.png'
import europe from '../../../assets/images/wmap-europe-opacity-glow.svg'

export function RemoveUserScreen() {
  const {currentAccount} = useSession()

  const [error, setError] = useState('')
  const [serviceUrl, setServiceUrl] = useState(
    currentAccount?.service || DEFAULT_SERVICE,
  )
  const [initialHandle] = useState(currentAccount?.handle || '')

  const {data: serviceDescription, refetch: refetchService} =
    useServiceQuery(serviceUrl)

  const onAttemptSuccess = () => {
    logEvent('signin:success', {
      isUsingCustomProvider: serviceUrl !== DEFAULT_SERVICE,
      timeTakenSeconds: 0,
      failedAttemptsCount: 0,
    })
  }

  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Remove Account</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      <Layout.Content>
        <View style={[a.px_lg, a.py_lg]}>
          <RemoveUserForm
            error={error}
            serviceUrl={serviceUrl}
            serviceDescription={serviceDescription}
            initialHandle={initialHandle}
            setError={setError}
            onAttemptFailed={() => {}}
            onAttemptSuccess={onAttemptSuccess}
            setServiceUrl={setServiceUrl}
            onPressBack={() => window.history.back()}
            onPressForgotPassword={() => {}}
            onPressRetryConnect={refetchService}
          />
        </View>
      </Layout.Content>
    </Layout.Screen>
  )
}

export const RemoveUser = ({onPressBack}: {onPressBack: () => void}) => {
  const {_} = useLingui()

  const {accounts} = useSession()
  const {requestedAccountSwitchTo} = useLoggedOutView()
  const requestedAccount = accounts.find(
    acc => acc.did === requestedAccountSwitchTo,
  )

  const [error, setError] = useState('')
  const [serviceUrl, setServiceUrl] = useState(
    requestedAccount?.service || DEFAULT_SERVICE,
  )
  const [initialHandle] = useState(requestedAccount?.handle || '')

  const {data: serviceDescription, refetch: refetchService} =
    useServiceQuery(serviceUrl)

  const handlePressBack = () => {
    onPressBack()
  }

  const onAttemptSuccess = () => {
    logEvent('signin:success', {
      isUsingCustomProvider: serviceUrl !== DEFAULT_SERVICE,
      timeTakenSeconds: 0,
      failedAttemptsCount: 0,
    })
  }

  const onAttemptFailed = () => {}

  const title = _(msg`Remove Account`)
  const description = _(msg`Scan to remove your account`)

  return (
    <Animated.View style={a.flex_1} entering={native(FadeIn.duration(90))}>
      <div
        style={{
          zIndex: -1,
          backgroundColor: '#000',
          backgroundImage: `url(${noice}), url(${europe}), linear-gradient(#000, #190031)`,
          backgroundPosition: '50%, 50%, 0 0',
          backgroundRepeat: 'no-repeat, no-repeat, repeat',
          backgroundSize: 'cover, cover, auto',
          backgroundAttachment: 'fixed, scroll, scroll',
          width: '100%',
          height: '100%',
          position: 'fixed',
        }}
      />
      <KeyboardAvoidingView
        testID="removeUser"
        behavior="padding"
        style={a.flex_1}>
        <LoggedOutLayout
          leadin=""
          title={title}
          description={description}
          scrollable>
          <LayoutAnimationConfig skipEntering>
            <ScreenTransition direction="Forward">
              <RemoveUserForm
                error={error}
                serviceUrl={serviceUrl}
                serviceDescription={serviceDescription}
                initialHandle={initialHandle}
                setError={setError}
                onAttemptFailed={onAttemptFailed}
                onAttemptSuccess={onAttemptSuccess}
                setServiceUrl={setServiceUrl}
                onPressBack={handlePressBack}
                onPressForgotPassword={() => {}}
                onPressRetryConnect={refetchService}
              />
            </ScreenTransition>
          </LayoutAnimationConfig>
        </LoggedOutLayout>
      </KeyboardAvoidingView>
    </Animated.View>
  )
}
